import {IO} from "lib/commons/io/io";
import {MsgLabel, MsgOver} from "back/commons/basis";
import {POPUP} from "back/commons/widgets/popups";
import {Action} from "lib/commons/actions";
import {JSX} from "lib/commons/xml/dom";
import {CDM} from "lib/commons/utils/cdm";
import {IPopupable} from "back/commons/widgets/popupable";
import {Desk} from "lib/commons/desk";
import {IReg, IRegPointer, IRegsPointer, REG} from "lib/commons/registry";
import {ISecurityCtxPointer} from "lib/commons/security";

import {EUserAspects} from "lib/core/user";
import {ERROR} from "lib/core/errorReport";

import "back/wsp/actions/actions_Perms";

import {IWspEnv, IWspUiEnv, JWspType, JWspTypeInst, WSP, Wsp, wspCd} from "lib/wsp/wsp";
import {WSPTYPE} from "lib/wsp/wspType";
import {WSP_DRV} from "lib/wsp/drv";
import {WSP_DRF} from "lib/wsp/drf";
import {IChainEnv} from "lib/wsp/chain";
import {srcRef} from "lib/wsp/src";

import {ChainAction} from "back/wsp/actions/chainActions";
import type {WspRemoveProps, WspsRemovePermanentlyProps} from "back/wsp/dialogs/wspProps";
import type {JWspAppDef} from "back/wsp/plugins/wspsPlg";
import type {OObjRolesMapInit} from "back/core/dialogs/objRolesMap";
import type {OWspsMonitoringInit, WspsMonitoring} from "back/wsp/widgets/wsp/wspsMonitoring";

/**
 * Contexte des actions associées à un atelier
 */
export interface IWspActionCtx extends IRegPointer<IWspUiEnv> {}

export interface ICreateWspCtx {
	/* True si undefined  */
	openWspAfterCreate?: boolean
}

/** Actions au niveau d'un atelier. */
export abstract class WspAction<E extends IWspActionCtx = IRegPointer<IWspUiEnv>> extends ChainAction<E> {
	isVisible(ctx: E): boolean {
		if (!ctx.reg?.env.wsp) return false;
		return super.isVisible(ctx);
	}
}

/**
 * Contexte des actions associées à plusieurs atelier
 */
export interface IWspsActionCtx extends IRegsPointer<IWspUiEnv> {}

export abstract class WspsAction<E extends IWspsActionCtx | IWspActionCtx> extends Action<E> {
	/** action affichée QUE dans les contextes ODB **/
	isBackendDb: boolean;
	/** action affichée QUE dans les contextes FS **/
	isBackendFs: boolean;
	/** */
	checkCompatKey: string | null;
	/** L'action n'est visible QUE dans le contexte IWspsActionCtx */
	onlyOnWsps: boolean = true;

	/** Liste des ateliers à traiter
	 * 	@return null si aucun atelier à traiter */
	public getWspRegs(ctx: E): IReg<IWspUiEnv>[] {
		let regs: IReg<IWspUiEnv>[] = [];
		if ((ctx as IWspsActionCtx).regs)
			(ctx as IWspsActionCtx).regs.forEach((entry) => {if (entry.env.wsp) regs.push(entry)});
		else if (!this.onlyOnWsps && (ctx as IWspActionCtx).reg && (ctx as IWspActionCtx).reg.env.wsp)
			regs.push((ctx as IWspActionCtx).reg)
		return regs.length > 0 ? regs : null;
	}

	isEnabled(ctx: E): boolean {
		if (this.checkCompatKey && !Desk.checkCompat(this.checkCompatKey)) return false;
		return super.isEnabled(ctx);
	}

	isVisible(ctx: E): boolean {
		let regs = this.getWspRegs(ctx);
		if (!regs) return false;
		if (this.isBackendDb && regs.findIndex((reg) => reg.env.wsp.backEnd === "fs") == -1) return false;
		if (this.isBackendFs && regs.findIndex((reg) => reg.env.wsp.backEnd === "odb") == -1) return false;
		return super.isVisible(ctx);
	}

	getDescription(ctx: E): string {
		if (this.checkCompatKey && !Desk.checkCompat(this.checkCompatKey))
			return "Ce navigateur n'est pas compatible avec cette fonction";
		return super.getDescription(ctx);
	}

	checkObjectRootVisiblePerm(ctx: E, perms?: string | string[], and?: boolean): boolean {
		if (!perms) perms = this._visPerms;
		let regs = this.getWspRegs(ctx);
		if (perms && regs && regs.findIndex((reg) => !reg.hasPerm(perms, (reg as ISecurityCtxPointer).securityCtx), and) > -1) return false;
		return true;
	}

	checkObjectRootPerm(ctx: E, perms?: string | string[], and?: boolean): boolean {
		if (!perms) perms = this._enablePerms;
		let regs = this.getWspRegs(ctx);
		if (perms && regs && regs.findIndex((reg) => !reg.hasPerm(perms, (reg as ISecurityCtxPointer).securityCtx), and) > -1) return false;
		return true;
	}

}

/** FS only. */
export class ScanFsUpdatesAction extends WspAction {
	constructor() {
		super();
		this._icon = "/@skin@/commons/icons/refresh.svg";
		this._label = "Rafraîchir l'atelier";
		this._description = "Détecte les changements de l'atelier réalisés directement dans le système de fichier.";
	}

	isInstantiable(ctx: IRegPointer<IWspUiEnv>): boolean {
		return ctx.reg.env.universe.config.backEnd === "fs";
	}

	execute(ctx: IRegPointer<IWspUiEnv>, ev?: Event): Promise<void> {
		const progress = POPUP.showProgress(ctx.reg.env.uiRoot, "Recherche des changements...");
		return ctx.reg.env.universe.wspServer.config.adminWspFsUrl.fetchVoid(IO.qs("cdaction", "ScanFsUpdates", "param", ctx.reg.env.wsp.code)).finally(() => progress.close());
	}
}

/**
 * Export d'un atelier (.scwsp)
 */
export class ExportWspAction<E extends IWspActionCtx = IRegPointer<IWspUiEnv>> extends WspAction<E> {

	constructor(id?: string) {
		super(id || "exportWsp");
		this._label = "Exporter une archive de l'atelier (.scwsp)";
		this._group = "importExport";
		this._enablePerms = "read.node.export";
	}

	execute(ctx: E, ev?: Event): any | "noPreventDefault" | "noStopPropag" | "noStopPropag-noPreventDefault" {
		const wsp = ctx.reg.env.wsp;
		const name = IO.getValidFileName(wsp.wspTitle, ".scwsp", 'dateTime');
		const url = ctx.reg.env.universe.config.wsps.exportUrl.resolve(IO.qs("cdaction", "Export", "param", wsp.code, "format", "jar", "scope", "node", "mode", "wspTree", "refUris", "", "downloadFileName", name)).url
		const a = <a href={url} download={name}/> as HTMLAnchorElement;
		a.click();
	}
}

/** Propriétés d'un atelier : lecture et/ou écriture */
export class OpenPropsWspAction extends WspAction {

	static SINGLETON = new OpenPropsWspAction();

	constructor(id?: string) {
		super(id || "openWspProps");
		this.checkCompatKey = "formElt";
		this._label = "Propriétés de l'atelier...";
		this._description = "Afficher les propriétés de l'atelier";
		this._group = "wsp";
		this.setIcon("/@skin@/wsp/actions/wspProps.svg");
		this.requireEnabledPerm("action.wsp#read.props");
	}

	async execute(ctx: IWspActionCtx, ev?: Event): Promise<void> {
		const reg = ctx.reg;
		const {WspEditProps} = await import("back/wsp/dialogs/wspProps.js");
		const wspTitle = reg.env.wsp.wspTitle;
		await POPUP.showDialog(new WspEditProps().initialize({
			reg: ctx.reg,
		}), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: wspTitle ? `Atelier '${wspTitle}'` : `Propriétés`}, closeButton: {}},
			resizer: {},
			initWidth: "min(90vw,60em)",
			initHeight: "90vh"
		}).onNextClose();
	}

}

/** Edition du modèle de plusieurs ateliers. */
export class OpenPropsWspsAction extends WspsAction<IWspsActionCtx> {
	static SINGLETON = new OpenPropsWspsAction();

	constructor(id?: string) {
		super(id || "openPropsWspsModel");
		this.checkCompatKey = "formElt";
		this._label = "Éditer les propriétés des ateliers...";
		this._description = "Éditer les propriétés des ateliers";
		this._group = "wsp";
		this.setIcon("/@skin@/wsp/actions/wspEditModel.svg");
		this.requireEnabledPerm("action.wsp#update.wsptype");
	}

	async execute(ctx: IWspsActionCtx, ev?: Event): Promise<void> {
		const {WspsEditModelProps} = await import("back/wsp/dialogs/wspProps.js");
		await POPUP.showDialog(new WspsEditModelProps().initialize(ctx), ev.composedPath()[0] as HTMLElement, {
			titleBar: {barLabel: {label: "Édition des propriétés des ateliers"}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			initWidth: "50vw",
			initHeight: "60vh"
		}).onNextClose();
	}

}

/** Migration automatique d'un atelier */
export class AutoMigrWspAction extends WspAction {

	constructor(id?: string) {
		super(id || "AautoMigrWsp");
		this._label = "Migrer l'atelier...";
		this._description = "Migrer les contenus de l'atelier";
		this._group = "wsp";
		this.setIcon("/@skin@/wsp/actions/wspMigr.svg");
		this.requireEnabledPerm("action.wsp#migrate");
	}

	isVisible(ctx: IWspActionCtx): boolean {
		if (ctx.reg.env.wsp?.migrating) return false;
		let infoWsp = ctx.reg.env.wsp?.infoWsp || ctx.reg.env.wsp?.infoWspError;
		if (!infoWsp?.isMigrationNeeded) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IWspActionCtx, ev?: Event): Promise<void> {
		const reg = ctx.reg;
		const wspTitle = reg.env.wsp.wspTitle;
		const msg = `Voulez-vous exécuter la migration des contenus de l'atelier '${wspTitle}' ?`;
		if (await POPUP.confirm(<div>
			<div>{msg}</div>
			<div><b>Vous ne pourrez pas annuler cette action.</b></div>
		</div>, reg.env.uiRoot, {okLbl: "Migrer les contenus", cancelLbl: "Annuler"})) {
			try {
				let wspMetaEditorDom: Document = await WSPTYPE.getUpdateEditor(ctx.reg.env.universe.wspServer, ctx.reg.env.wsp.code);
				let vUnknownModels: string[] = [];
				// Construction du wspTypeInst cible (on ne conserve que les noeuds sélectionnés non obsolètes, ou "newest")
				let wspTypeInst: JWspTypeInst = {wspOptions: []} as JWspTypeInst;
				let wspTypeDef = wspMetaEditorDom.querySelector("wspTypeDef[newest], wspTypeDef[selected]:not([obsolete])");
				if (wspTypeDef) {
					wspTypeInst.wspType = {
						key: wspTypeDef.getAttribute("key"),
						lang: wspTypeDef.getAttribute("lang"),
						version: wspTypeDef.getAttribute("version"),
						title: wspTypeDef.getAttribute("title"),
						uri: wspTypeDef.getAttribute("uri")
					} as JWspType;
					if (wspTypeDef.getAttribute("unknown"))
						vUnknownModels.push(wspTypeInst.wspType.title)
					let wspOptionsDef = wspMetaEditorDom.querySelectorAll("wspOptionDef[newest], wspOptionDef[selected]:not([obsolete])");
					wspOptionsDef.forEach((wspOptionDef) => {
						wspTypeInst.wspOptions.push({
							key: wspOptionDef.getAttribute("key"),
							lang: wspOptionDef.getAttribute("lang"),
							version: wspOptionDef.getAttribute("version"),
							title: wspOptionDef.getAttribute("title"),
							uri: wspOptionDef.getAttribute("uri"),
							unknown: wspOptionDef.getAttribute("unknown")
						} as JWspType);
						if (wspOptionDef.getAttribute("unknown"))
							vUnknownModels.push(wspOptionDef.getAttribute("title"))
					});

					if (vUnknownModels.length) {
						// Refus de migration dont la cible est auto-déduite si la cible contient des modeles/exts inconnues : migr manuelle explicite par l'auteur
						const unknownList = vUnknownModels.join(", ");
						POPUP.showNotifError(`La migration de l'atelier '${wspTitle}' ne peut avoir lieu car certaines extensions sont actuellement inconnues : "${unknownList}".\nVeuillez choisir le modèle cible et ses extensions.`, reg.env.uiRoot)
					} else {
						// - Exécution de la migration
						await reg.env.wsp.updateWspType(wspTypeInst, true);
						//POPUP.showNotifInfo(`La migration de l'atelier '${wspTitle}' a été menée jusqu'à son terme.`, reg.env.uiRoot, {noCloseBtn: true, autoHide: 5000}) FAUX : action asynchrone => en attente des remontées ws de fin de migration
					}
				} else
					await POPUP.showNotifError("La cible de migration n'a pas pu être déterminée. Veuillez ajuster le modèle documentaire de l'atelier via la fenêtre d'édition des propriétés.", reg.env.uiRoot, {noCloseBtn: true});
			} catch (e) {
				await ERROR.report(`La migration de l'atelier '${wspTitle}' n'a pas pu aboutir. Veuillez réessayer ultérieurement.`, e);
			}
		}

	}

}

/** Permissions affectées sur un atelier */
export class OpenPermsWspAction extends WspAction {

	static SINGLETON = new OpenPermsWspAction();

	constructor(id?: string) {
		super(id || "openPermsWsp");
		this.checkCompatKey = "formElt";
		this._label = "Permissions sur l'atelier...";
		this._description = "Définir les permissions de cet atelier";
		this._group = "wsp";
		this.setIcon("/@skin@/wsp/actions/wspPerms.svg");
		this.requireEnabledPerm(["action.wsp#set.perms", "admin.node.configRoles.byAdmin"]);
		this.setVisible((ctx) => ctx.reg.env.universe.adminUsers?.hasAspect(EUserAspects.rolable) ? true : false);
	}

	async execute(ctx: IOpenPermsWspActionCtx, ev?: Event): Promise<void> {
		const reg = ctx.reg;
		const {ObjRolesMapEdit} = await import("back/core/dialogs/objRolesMap.js");
		const wspTitle = reg.env.wsp.wspTitle;
		await POPUP.showDialog(new ObjRolesMapEdit<IWspActionCtx, IWspUiEnv>().initialize({
			reg: ctx.reg,
			uiContext: "popup",
			rolesUiLayerSng: "wsp",
			preSelectAccounts: ctx.preSelectAccounts,
			objThis: ctx,
			objEditRolesMap: (objThis, rolesMap) => {return WSP.srcSetRoles(ctx.reg.env.wsp, ctx.reg.env.uiRoot, "", rolesMap)},
			objEditRolesMapPerms: ["action.wsp#set.perms", "admin.node.configRoles.byAdmin"],
			objFetchRolesMap: (objThis) => {return WSP.srcGetRoles(ctx.reg.env.wsp, ctx.reg.env.uiRoot, "")},
			objFetchRolesMapForAccounts: (objThis, pAccounts: string[]) => {return WSP.srcGetRolesForAccounts(ctx.reg.env.wsp, ctx.reg.env.uiRoot, "", pAccounts)},
		} as OObjRolesMapInit<IWspActionCtx, IWspUiEnv>), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: wspTitle ? `Permissions définies sur l'atelier '${wspTitle}'` : `Permissions définies sur l'atelier`}, closeButton: {}},
			resizer: {},
			initWidth: "40em",
			initHeight: "40em"
		}).onNextClose();
	}

}

export interface IOpenPermsWspActionCtx extends IWspActionCtx {
	preSelectAccounts?: string[]
}

/** Suppression d'un atelier. */
export class DeleteWspAction extends WspAction {
	static SINGLETON = new DeleteWspAction();

	constructor(id?: string) {
		super(id || "deleteWsp");
		this._label = "Supprimer l'atelier...";
		this._description = "Supprimer l'atelier";
		this._group = "del";
		this.setIcon("/@skin@/wsp/actions/wspDelete.svg");
		this.requireEnabledPerm("action.wsp#delete");
	}

	async execute(ctx: IWspActionCtx, ev?: Event): Promise<boolean> {
		const {WspRemoveProps} = await import("back/wsp/dialogs/wspProps.js");
		return await POPUP.showDialog<boolean>(new WspRemoveProps().initialize(ctx), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: "Suppression d'un atelier"}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			fixSize: false
		}).onNextClose();
	}
}


/** Suppression de plusieurs ateliers. */
export class DeleteWspsAction extends WspsAction<IWspsActionCtx> {
	static SINGLETON = new DeleteWspsAction();

	constructor(id?: string) {
		super(id || "deleteWsp");
		this._label = "Supprimer les ateliers...";
		this._description = "Supprimer les ateliers";
		this._group = "del";
		this.setIcon("/@skin@/wsp/actions/wspDelete.svg");
		this.requireEnabledPerm("action.wsp#delete");
	}

	async execute(ctx: IWspsActionCtx, ev?: Event): Promise<void> {
		const {WspsRemoveProps} = await import("back/wsp/dialogs/wspProps.js");
		await POPUP.showDialog(new WspsRemoveProps().initialize(ctx), ev.composedPath()[0] as HTMLElement, {
			titleBar: {barLabel: {label: "Suppression d'ateliers"}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			initWidth: "50vw",
			initHeight: "60vh"
		}).onNextClose();
	}

}

/** Trashed : suppression */
export class DeletePermanentlyWspsAction extends WspsAction<(IWspsActionCtx | IWspActionCtx) & IRegPointer<IChainEnv>> {
	static SINGLETON = new DeletePermanentlyWspsAction();

	constructor(id?: string) {
		super(id || "deletePermanentlyWsps");
		this.setLabel((ctx: (IWspsActionCtx | IWspActionCtx) & IRegPointer<IChainEnv>) => {
			let count = (ctx as IWspsActionCtx).regs ? (ctx as IWspsActionCtx).regs.length : ((ctx as IWspActionCtx).reg ? 1 : 0);
			return count > 1 ? `Supprimer définitivement les ${count} ateliers` : "Supprimer définitivement l'atelier";
		});
		this._group = "adminWsp";
		this.onlyOnWsps = false;
		this.requireEnabledPerm("action.wsp#deletePermanently");
		this.setIcon("/@skin@/wsp/actions/wspDeletePermanently.svg");
	}

	async execute(ctx: (IWspsActionCtx | IWspActionCtx) & IRegPointer<IChainEnv>, ev?: Event): Promise<void> {
		const {WspsRemovePermanentlyProps} = await import("back/wsp/dialogs/wspProps.js");
		await POPUP.showDialog(new WspsRemovePermanentlyProps().initialize({regs: this.getWspRegs(ctx), reg: ctx.reg}), ev.composedPath()[0] as HTMLElement, {
			titleBar: {barLabel: {label: "Suppression définitive d'ateliers"}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			initWidth: "50vw",
			initHeight: "60vh"
		}).onNextClose();
	}

}

REG.reg.registerSkin('c-wsps-action', 1, /* language=CSS */ `
	wsps-monitoring {
		margin: 0 2em;
		max-height: 15em;
		overflow: auto;
	}
`);

/** Trashed : restauration */
export class RestoreWspsAction extends WspsAction<(IWspsActionCtx | IWspActionCtx) & IRegPointer<IChainEnv>> {
	static SINGLETON = new RestoreWspsAction();

	constructor(id?: string) {
		super(id || "restoreWsps");
		this.setLabel((ctx: (IWspsActionCtx | IWspActionCtx) & IRegPointer<IChainEnv>) => {
			let count = (ctx as IWspsActionCtx).regs ? (ctx as IWspsActionCtx).regs.length : ((ctx as IWspActionCtx).reg ? 1 : 0);
			return count > 1 ? `Restaurer les ${count} ateliers` : "Restaurer l'atelier";
		});
		this._group = "adminWsp";
		this.onlyOnWsps = false;
		this.requireEnabledPerm("action.wsp#restore");
		this.setIcon("/@skin@/wsp/actions/wspRestore.svg");
	}

	/**
	 * @return : false si aucune restauration n'a eu lieu
	 * @param ctx
	 * @param ev
	 */
	async execute(ctx: (IWspsActionCtx | IWspActionCtx) & IRegPointer<IChainEnv>, ev?: Event): Promise<boolean> {
		let wspTitleErrosArray: string[] = [];
		let dialog: IPopupable;
		try {
			let regs = this.getWspRegs(ctx);

			let wsps: Wsp[] = [];
			regs.forEach((entry) => {wsps.push(entry.env.wsp)});
			wsps = WSP.sortWspsByDeep(wsps);
			let count = wsps.length;

			// Controle : si drv/drf, le drfRefWsp ou drvMasterWsp ne doit pas être en corbeille
			//(await WSP.listWsps(this.reg.env.universe.wspServer, {inTrash: true, fields: ["srcDt", "srcRoles", "srcRi"]})).wsps
			let mandatWspsTitles: string[] = [];
			if (wsps.find((wsp) => (wsp.infoWsp || wsp.infoWspError).props.drvMasterWsp || (wsp.infoWsp || wsp.infoWspError).props.drfRefWsp ? true : false)) {
				await Promise.all(wsps.map(async (wsp) => {
					let parentWspCd = (wsp.infoWsp || wsp.infoWspError).props.drvMasterWsp || (wsp.infoWsp || wsp.infoWspError).props.drfRefWsp;
					if (parentWspCd) {
						let parentWsp = new Wsp(ctx.reg.env.universe.wspServer, parentWspCd);
						await parentWsp.waitForLoad();
						if (parentWsp.isDeleted && !wsps.find((wsp) => wsp.code === parentWspCd))
							mandatWspsTitles.push(parentWsp.wspTitle)
					}
				}))
			}
			if (mandatWspsTitles.length > 0) {
				let list = mandatWspsTitles.join(", ");
				await ERROR.show(`Les ateliers suivants doivent être restaurés au préalable : '${list}'.`);
				return false;
			}

			if (await POPUP.confirm(count > 1 ? `Voulez-vous vraiment restaurer les '${count}' ateliers ?` : "Voulez-vous vraiment restaurer l'atelier ?", window.document, {cancelLbl: "Annuler", okLbl: "Restaurer"})) {
				const {WspsMonitoring} = await import("back/wsp/widgets/wsp/wspsMonitoring.js");
				const wspsMonitoring = <WspsMonitoring î={{
					wsps: wsps,
					defaultStateKey: "notStart",
					states: [
						{key: "notStart", level: "disabled", label: "En attente"},
						{key: "toRemove", level: "disabled", label: "À restaurer"},
						{key: "pending", level: "pending", label: "En cours..."},
						{key: "restored", level: "success", label: "Restauré"},
						{key: "failed", level: "error", label: "Échec"}
					]
				} as OWspsMonitoringInit}/> as WspsMonitoring;

				dialog = POPUP.showDialog(<div>
					<MsgLabel label="Restauration en cours..."/>
					{wspsMonitoring}
				</div>, window.document, {
					skinOver: "c-wsps-action",
					fixSize: false,
					titleBar: null,
					initWidth: '50%',
				});
				let errorsArray: any[] = [];
				for (const entry in wsps) {
					let wsp = wsps[entry];
					wspsMonitoring.updateWspStatus(wsp.code, "pending");
					try {
						await wsp.restore();
						wspsMonitoring.updateWspStatus(wsp.code, "restored");
						await new Promise(resolve => setTimeout(resolve, 1000));
					} catch (e) {
						wspsMonitoring.updateWspStatus(wsp.code, "failed");
						wspTitleErrosArray.push(wsp.wspTitle);
						errorsArray.push(e);
					}
				}
				await new Promise(resolve => setTimeout(resolve, errorsArray.length > 0 ? 3000 : 1000));
				if (errorsArray.length > 0) throw errorsArray;
				return true;
			}
		} catch (e) {
			let list = wspTitleErrosArray.join(", ");
			await ERROR.report(list ? "Restauration des ateliers en échec." : `Restauration des ateliers ${list} en échec.`, e);
			return true;
		} finally {
			if (dialog) dialog.close();
		}
	}

}

/** Remove cache */
export class RemoveCacheWspAction extends WspAction {
	static SINGLETON = new RemoveCacheWspAction();

	constructor(id?: string) {
		super(id || "removeCacheWsp");
		this._label = "Vider le cache de l'atelier";
		this._description = "Cache des transformations de ressources...";
		this._group = "adminWsp";
		this.requireEnabledPerm("action.wsp#removeCache");
	}

	async execute(ctx: IRegPointer<IWspUiEnv | IWspEnv>, ev?: Event): Promise<void> {
		try {
			new MsgOver().setCustomMsg("Suppression du cache de l'atelier en cours...").showMsgOver((ctx.reg.env as IWspUiEnv).uiRoot || window.document.documentElement).waitFor(
				WSP.removeCache(ctx.reg.env.wsp)
			);
		} catch (e) {
			await ERROR.report(`La suppression du cache n'a pas pu aboutir. Veuillez réessayer ultérieurement.`, e);
		}
	}

}

/** reload wsp */
export class ReloadWspAction extends WspAction {
	static SINGLETON = new ReloadWspAction();

	constructor(id?: string) {
		super(id || "reloadWsp");
		this._label = "Recharger l'atelier";
		this._group = "adminWsp";
		this.requireEnabledPerm("action.wsp#reload");
	}

	async execute(ctx: IRegPointer<IWspUiEnv | IWspEnv>, ev?: Event): Promise<void> {
		try {
			new MsgOver().setCustomMsg("Rechargement de l'atelier en cours...").showMsgOver((ctx.reg.env as IWspUiEnv).uiRoot || window.document.documentElement).waitFor(
				WSP.reloadWsp(ctx.reg.env.wsp)
			);
		} catch (e) {
			await ERROR.report(`Le chargement de l'atelier n'a pas pu aboutir. Veuillez réessayer ultérieurement.`, e);
		}
	}

}


/** Création d'un atelier à partir d'un autre
 *		IRegPointer<IWspUiEnv> permet de créer un atelier en se basant sur les propriétés existantes d'un autre atelier
 * */
export class CreateWspFromWspAction extends WspAction {
	static SINGLETON = new CreateWspFromWspAction();

	constructor(id?: string) {
		super(id || "createWspFromWsp");
		this.checkCompatKey = "formElt";
		this.setIcon("/@skin@/wsp/actions/wspCreateFromWsp.svg");
		this.requireEnabledPerm("action.chain#create.wsp");
	}

	getLabel(ctx: IRegPointer<IWspUiEnv>): string {
		let wspTitle = ctx.reg.env.wsp.wspTitle;
		return `Dupliquer la configuration de l'atelier '${wspTitle}'...`;
	}

	getDescription(ctx: IRegPointer<IWspUiEnv>): string {
		let wspTitle = ctx.reg.env.wsp.wspTitle;
		return `Créer un atelier à partir de la configuration de l'atelier ${wspTitle}'...`;
	}

	async execute(ctx: IRegPointer<IWspUiEnv> & ICreateWspCtx, ev?: Event): Promise<Wsp | null> {
		const reg = ctx.reg;
		const {WspCreateProps} = await import("back/wsp/dialogs/wspProps.js");
		const wsp = await POPUP.showDialog<Wsp | null>(new WspCreateProps().initialize({
			reg: reg,
		}), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: "Création d'un atelier"}, closeButton: {}},
			resizer: {},
			initWidth: '45em',
			initHeight: '70vh',
			viewPortX: "middle",
			viewPortY: "middle"
		}).onNextClose();
		this.openWspAfterCreate(ctx, wsp);
		return wsp;
	}
}

/** Création d'un atelier DRF */
export class CreateDrfWspAction extends WspAction {
	static SINGLETON = new CreateDrfWspAction();

	constructor(id?: string) {
		super(id || "createDrfWsp");
		this.checkCompatKey = "formElt";
		this._label = "Créer un atelier calque de travail...";
		this._description = "Créer un atelier calque de travail";
		this._group = "add";
		this.setIcon("/@skin@/wsp/actions/wspCreate.svg");
		this.requireVisiblePerm("action.wsp#create.wsp.drf");
	}

	isVisible(ctx: IRegPointer<IWspUiEnv>): boolean {
		if (!ctx.reg.env.wsp || !ctx.reg.env.wsp.hasFeature('drf')) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IWspActionCtx & ICreateWspCtx, ev?: Event): Promise<Wsp | null> {
		const reg = ctx.reg;
		const wspTitle = ctx.reg.env.wsp.wspTitle;
		const {WspCreatDrfProps} = await import("back/wsp/dialogs/wspProps.js");
		const result = await POPUP.showDialog<Wsp | undefined>(new WspCreatDrfProps().initialize({
			reg: ctx.reg,
		}), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: `Création d'un atelier calque de travail de '${wspTitle}'`}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			fixSize: false
		}).onNextClose();
		this.openWspAfterCreate(ctx, result);
		return result;
	}

}

/** Création d'un atelier DRV */
export class CreateDrvWspAction extends WspAction {
	static SINGLETON = new CreateDrvWspAction();

	constructor(id?: string) {
		super(id || "createDrvWsp");
		this.checkCompatKey = "formElt";
		this._label = "Créer un atelier calque de dérivation...";
		this._description = "Créer un atelier calque de dérivation";
		this._group = "add";
		this.setIcon("/@skin@/wsp/actions/wspCreate.svg");
		this.requireVisiblePerm("action.wsp#create.wsp.drv");
	}

	isVisible(ctx: IWspActionCtx): boolean {
		// Pas de dérivation de dérivation, pas de dérivation sur les ateliers draft
		const wsp = ctx.reg.env.wsp;
		if (!wsp || !wsp.hasFeature('drv')) return false;
		const infoWsp = wsp.infoWsp || wsp.infoWspError;
		if (!infoWsp || WSP_DRV.isDrv(infoWsp.props)) return false;
		if (!infoWsp || WSP_DRF.isDrf(infoWsp.props)) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IWspActionCtx & ICreateWspCtx, ev?: Event): Promise<Wsp | null> {
		const reg = ctx.reg;
		const wspTitle = ctx.reg.env.wsp.wspTitle;
		const {WspCreatDrvProps} = await import("back/wsp/dialogs/wspProps.js");
		const result = await POPUP.showDialog<Wsp>(new WspCreatDrvProps().initialize({
			reg: ctx.reg,
		}), ctx.reg.env.uiRoot, {
			titleBar: {barLabel: {label: `Création d'un atelier calque de dérivation de '${wspTitle}'`}, closeButton: {}},
			resizer: {},
			viewPortX: "middle",
			viewPortY: "middle",
			fixSize: false
		}).onNextClose();
		this.openWspAfterCreate(ctx, result);
		return result;
	}

}


/** Svc d'accès à item étranger dans le contexte de son propre atelier. */
export type IOpenForeignItemSvc = (reg: IReg<IChainEnv>, wspCd: wspCd, srcRef: srcRef) => Promise<void>

/** IOpenForeignItemSvc: Impl par défaut dans un browser : ouverture dans un nouvel onglet. */
REG.reg.registerSvc("openForeignItem", 1, function (reg: IReg<IChainEnv>, wspCd: wspCd, srcRef: srcRef) {
	window.open(`#${CDM.stringify({u: reg.env.universe.getId(), wsp: wspCd, srcRef: srcRef} as JWspAppDef)}`, "_blank");
} as IOpenForeignItemSvc);